import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import { addDays, endOfDay, startOfDay, startOfMonth, endOfMonth, addMonths, startOfWeek, endOfWeek } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { ReactComponent as CalendarSvg } from './icon-calendar.svg';
import moment from 'moment';
import Styles from './styles.module.scss';
import './styles.scss';
import { IReduxAppState } from '../../../../../reducers';
interface IProps {
    onChange: (startDate: Date | number, endDate: Date | number) => void;
    minDate?: Date;
    maxDate?: Date;
    locale?: any;
}

const defineds = {
    startOfWeek: startOfWeek(new Date()),
    endOfWeek: endOfWeek(new Date()),
    startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
    endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
    startOfToday: startOfDay(new Date()),
    endOfToday: endOfDay(new Date()),
    startOfYesterday: startOfDay(addDays(new Date(), -1)),
    endOfYesterday: endOfDay(addDays(new Date(), -1)),
    startOfMonth: startOfMonth(new Date()),
    endOfMonth: endOfMonth(new Date()),
    startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
    endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
};

const DatesPicker = (props: IProps) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(new Date().setHours(0, 0, 0, 0));
    const [endDate, setEndDate] = useState(new Date().setHours(23, 59, 59, 59));
    const [newStartDate, setNewStartDate] = useState(new Date().setHours(23, 59, 59, 59));
    const [newEndDate, setNewEndDate] = useState(new Date().setHours(23, 59, 59, 59));
    const [visible, setVisible] = useState(false);
    const [friendlyDate, setFriendlyDate] = useState<string>('Today');
    const currentLanguage = useSelector((state: IReduxAppState) => state.language.current);
    const generateFriendlyDate = (startDate: Date | number, endDate: Date | number) => {
        const format = 'dddd, LL';
        const mStart = moment(startDate);
        const mEnd = moment(endDate);
        const mToday = moment(new Date());
        if (mStart.date() === mEnd.date()) {
            if (mToday.date() === mStart.date()) {
                return t('datePicker.today');
            }
            return mStart.format(format);
        }
        return `${mStart.format(format)} > ${mEnd.format(format)}`;
    };

    const staticRanges = createStaticRanges([
        {
            label: t('datePicker.today'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfToday,
                endDate: defineds.endOfToday,
            }),
        },
        {
            label: t('datePicker.yesterday'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfYesterday,
                endDate: defineds.endOfYesterday,
            }),
        },
        {
            label: t('datePicker.thisWeek'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfWeek,
                endDate: defineds.endOfWeek,
            }),
        },
        {
            label: t('datePicker.lastWeek'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfLastWeek,
                endDate: defineds.endOfLastWeek,
            }),
        },
        {
            label: t('datePicker.thisMonth'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfMonth,
                endDate: defineds.endOfMonth,
            }),
        },
        {
            label: t('datePicker.lastMonth'),
            isSelected: () => false,
            range: () => ({
                startDate: defineds.startOfLastMonth,
                endDate: defineds.endOfLastMonth,
            }),
        },
    ]);

    const selectionRange = {
        startDate: new Date(newStartDate),
        endDate: new Date(newEndDate),
        key: 'selection',
    };
    const setDateRangeState = (value: any) => {
        setNewStartDate(value.selection.startDate);
        setNewEndDate(value.selection.endDate);
    };
    const applyDateRange = () => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        props.onChange(newStartDate, newEndDate);
        closeDatePicker();
    };
    const cancelDateRange = () => {
        setNewStartDate(startDate);
        setNewEndDate(endDate);
        closeDatePicker();
    };
    const openDatePicker = () => setVisible(true);
    const closeDatePicker = () => setVisible(false);
    useEffect(() => {
        setFriendlyDate(generateFriendlyDate(startDate, endDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, currentLanguage]);

    return (
        <div>
            <div onClick={openDatePicker} className={Styles.datePickerToggler}>
                <CalendarSvg />
                <div>
                    <span>{friendlyDate}</span>
                    <span className={Styles.IconWrapper}>
                        <ExpandMore />
                    </span>
                </div>
            </div>
            {visible && (
                <div className={Styles.Wrapper}>
                    <div className={Styles.Cover} onClick={cancelDateRange}></div>
                    <div className={Styles.Picker}>
                        <DateRangePicker
                            ranges={[selectionRange]}
                            onChange={setDateRangeState}
                            inputRanges={[]}
                            locale={props.locale}
                            staticRanges={staticRanges}
                        />
                        <div className={Styles.ButtonsList}>
                            <div className={Styles.ButtonWrapper}>
                                <Button onClick={applyDateRange} variant="contained" color="primary">
                                    {t('misc.apply')}
                                </Button>
                            </div>
                            <div className={Styles.ButtonWrapper}>
                                <Button onClick={cancelDateRange} variant="contained" color="default">
                                    {t('misc.cancel')}
                                </Button>
                            </div>
                            <div style={{ clear: 'both' }}></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatesPicker;
